import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaGem, FaCrown, FaRing } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';

const HeroSection = () => {
  const { isDarkMode } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: 'Exquisite Gold Collection',
      subtitle: 'Timeless Elegance',
      description: 'Discover our premium 22K gold jewellery crafted with perfection',
      image: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=1200',
      cta: 'Explore Gold Collection',
      link: '/category/gold',
      icon: <FaCrown />
    },
    {
      id: 2,
      title: 'Diamond Dreams',
      subtitle: 'Brilliance Redefined',
      description: 'Luxury diamond jewellery that captures hearts and moments',
      image: 'https://images.unsplash.com/photo-1602173574767-37ac01994b2a?w=1200',
      cta: 'Shop Diamonds',
      link: '/category/diamond',
      icon: <FaGem />
    },
    {
      id: 3,
      title: 'Bridal Collection 2024',
      subtitle: 'Your Perfect Day',
      description: 'Make your wedding unforgettable with our exclusive bridal sets',
      image: 'https://images.unsplash.com/photo-1506630448388-4e683c67ddb0?w=1200',
      cta: 'View Bridal Collection',
      link: '/collections/bridal',
      icon: <FaRing />
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section className="hero-section position-relative overflow-hidden" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      background: isDarkMode ? 'var(--bg-secondary)' : 'var(--bg-primary)'
    }}>
      {/* Background Image with Parallax Effect */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          className="position-absolute w-100 h-100"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1 }}
          style={{
            backgroundImage: `url(${currentSlideData.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'brightness(0.6)'
          }}
        />
      </AnimatePresence>

      {/* Overlay Gradient */}
      <div className="position-absolute w-100 h-100" style={{
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(13, 13, 13, 0.8) 0%, rgba(13, 13, 13, 0.4) 100%)'
          : 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 100%)'
      }} />

      {/* Content */}
      <div className="container position-relative" style={{ zIndex: 2 }}>
        <div className="row align-items-center min-vh-100">
          <div className="col-lg-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.8 }}>

                {/* Icon */}
                <motion.div
                  className="mb-4"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}>
                  <span style={{
                    fontSize: '60px',
                    color: 'var(--primary-gold)',
                    display: 'inline-block'
                  }}>
                    {currentSlideData.icon}
                  </span>
                </motion.div>

                {/* Subtitle */}
                <motion.p
                  className="text-uppercase mb-3"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  style={{
                    color: 'var(--primary-gold)',
                    letterSpacing: '3px',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                  {currentSlideData.subtitle}
                </motion.p>

                {/* Title */}
                <motion.h1
                  className="display-luxury mb-4 text-white"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  style={{
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
                  }}>
                  {currentSlideData.title}
                </motion.h1>

                {/* Description */}
                <motion.p
                  className="lead mb-5 text-white"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  style={{
                    fontSize: '20px',
                    fontWeight: '300',
                    maxWidth: '600px'
                  }}>
                  {currentSlideData.description}
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                  className="d-flex flex-wrap gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}>
                  <Link
                    to={currentSlideData.link}
                    className="btn btn-luxury btn-lg d-inline-flex align-items-center">
                    {currentSlideData.cta}
                    <FaArrowRight className="ms-2" />
                  </Link>
                  <Link
                    to="/collections"
                    className="btn btn-outline-light btn-lg"
                    style={{
                      borderRadius: '50px',
                      padding: '12px 30px',
                      borderWidth: '2px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                    View All Collections
                  </Link>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right Side Decorative Elements */}
          <div className="col-lg-5 d-none d-lg-block">
            <motion.div
              className="position-relative"
              animate={{
                y: [0, -20, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}>
              <div className="floating">
                <div className="shine" style={{
                  width: '300px',
                  height: '300px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, transparent 70%)',
                  filter: 'blur(40px)',
                  margin: '0 auto'
                }} />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="position-absolute bottom-0 start-50 translate-middle-x mb-5">
          <div className="d-flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="border-0"
                style={{
                  width: currentSlide === index ? '40px' : '10px',
                  height: '10px',
                  borderRadius: '5px',
                  background: currentSlide === index
                    ? 'var(--primary-gold)'
                    : 'rgba(255, 255, 255, 0.5)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Bottom Wave */}
      <div className="position-absolute bottom-0 w-100">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: 'block' }}>
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z"
            fill={isDarkMode ? 'var(--bg-primary)' : 'var(--bg-secondary)'}
          />
        </svg>
      </div>

      {/* Floating Stats */}
      <motion.div
        className="position-absolute end-0 top-50 translate-middle-y me-5 d-none d-xl-block"
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1, duration: 0.8 }}>
        <div className="glass-card p-4" style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px'
        }}>
          <div className="text-center mb-3">
            <h3 className="gold-text mb-0">50K+</h3>
            <small className="text-white">Happy Customers</small>
          </div>
          <div className="text-center mb-3">
            <h3 className="gold-text mb-0">25+</h3>
            <small className="text-white">Years of Trust</small>
          </div>
          <div className="text-center">
            <h3 className="gold-text mb-0">100%</h3>
            <small className="text-white">Certified Jewellery</small>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;